import React, { useState, useRef, useEffect, useContext } from 'react'
import axios from 'axios'
import { StudyTelemetryContext } from '../telemetry/StudyTelemetryContext'
import { useStudyRegion } from '../telemetry/StudyRegionContext'
import { CHAT_EVENTS, REGIONS } from '../telemetry/telemetryEvents'

const BASE = process.env.REACT_APP_HOST_API + '/study'

// Cosmo tutor chat. The system prompt is server-side (session-keyed); the client
// sends only the message + current code + prior turns. Fresh per learning task.
// Instrumented to the same CHAT_* contract as the workspace ChatWindow.
export default function CosmoPanel ({ participantId, code }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', text: "Hi, I'm Cosmo. Ask me anything about this task — I'll help you reason through it, but I won't hand you the full solution." }
  ])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const logRef = useRef(null)
  const containerRef = useRef(null)

  const { logTelemetryEvent } = useContext(StudyTelemetryContext)

  // Register Region
  useStudyRegion(REGIONS.CHAT_WINDOW, containerRef, 10)

  // Track last selections to avoid duplicates
  const lastInputSelection = useRef('')
  const lastHistorySelection = useRef('')

  const isPasting = useRef(false)

  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight }, [messages, busy])

  // Track selection in chat history
  useEffect(() => {
    const el = logRef.current
    if (!el) return

    const handleHistorySelection = () => {
      const selectedText = window.getSelection().toString()
      if (selectedText && selectedText !== lastHistorySelection.current) {
        lastHistorySelection.current = selectedText
        logTelemetryEvent(CHAT_EVENTS.SELECT_HISTORY, {
          selected_text: selectedText,
          timestamp: Date.now()
        })
      } else if (!selectedText) {
        lastHistorySelection.current = ''
      }
    }

    el.addEventListener('mouseup', handleHistorySelection)
    el.addEventListener('keyup', handleHistorySelection)

    return () => {
      el.removeEventListener('mouseup', handleHistorySelection)
      el.removeEventListener('keyup', handleHistorySelection)
    }
  }, [logTelemetryEvent])

  const send = async () => {
    const text = input.trim()
    if (!text || busy) return
    const history = messages.map(m => ({ role: m.role, content: m.text }))
    setMessages(m => [...m, { role: 'user', text }])
    setInput(''); setBusy(true)

    const startTime = Date.now()
    logTelemetryEvent(CHAT_EVENTS.QUERY, {
      text: text,
      length: text.length,
      timestamp: startTime
    })

    try {
      const r = await axios.post(`${BASE}/tutor`, { participantId, message: text, code, history })
      const endTime = Date.now()
      setMessages(m => [...m, { role: 'assistant', text: r.data.content }])

      logTelemetryEvent(CHAT_EVENTS.RESPONSE, {
        content: r.data.content,
        length: (r.data.content || '').length,
        latency_ms: endTime - startTime,
        timestamp: endTime
      })
    } catch (e) {
      const message = e.response?.data?.error || e.message
      setMessages(m => [...m, { role: 'assistant', text: 'Tutor error: ' + message }])
      logTelemetryEvent(CHAT_EVENTS.ERROR, { error: message })
    } finally { setBusy(false) }
  }

  const handleInputChange = (e) => {
    const val = e.target.value
    const prevLength = input.length

    setInput(val)

    // Only log TYPE if text was added (not deleted or pasted)
    if (val.length > prevLength && !isPasting.current) {
      logTelemetryEvent(CHAT_EVENTS.TYPE, {
        text: val,
        timestamp: Date.now()
      })
    }

    isPasting.current = false
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }

    if (e.key === 'Backspace' || e.key === 'Delete') {
      const start = e.target.selectionStart
      const end = e.target.selectionEnd
      const isRangeDelete = end - start > 0

      let predictedText = ''

      if (isRangeDelete) {
        predictedText = input.slice(0, start) + input.slice(end)
      } else {
        if (e.key === 'Backspace') {
          if (start === 0) return // Nothing to backspace
          predictedText = input.slice(0, start - 1) + input.slice(start)
        } else {
          predictedText = input.slice(0, start) + input.slice(start + 1)
        }
      }

      logTelemetryEvent(CHAT_EVENTS.DELETE, {
        timestamp: Date.now(),
        cursor_position: start,
        selection_end: end,
        text_before: input,
        text_after: predictedText,
        is_range_delete: isRangeDelete,
        key_used: e.key
      })
    }
  }

  const handleInputSelect = (e) => {
    const start = e.target.selectionStart
    const end = e.target.selectionEnd
    const selectedText = e.target.value.substring(start, end)

    if (selectedText && selectedText !== lastInputSelection.current) {
      lastInputSelection.current = selectedText
      logTelemetryEvent(CHAT_EVENTS.SELECT_INPUT, {
        selected_text: selectedText,
        timestamp: Date.now()
      })
    } else if (!selectedText) {
      lastInputSelection.current = ''
    }
  }

  const handlePaste = (e) => {
    isPasting.current = true
    const pastedText = e.clipboardData.getData('text')
    logTelemetryEvent(CHAT_EVENTS.PASTE, {
      pasted_text: pastedText,
      timestamp: Date.now()
    })
  }

  const handleCopy = (e) => {
    let selectedText = ''

    // Copying from the Input Box
    if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') {
      const start = e.target.selectionStart
      const end = e.target.selectionEnd
      selectedText = e.target.value.substring(start, end)

      if (selectedText && selectedText.length > 0) {
        logTelemetryEvent(CHAT_EVENTS.COPY_INPUT, {
          selected_text: selectedText,
          timestamp: Date.now()
        })
      }
    }
    // Copying from Chat History
    else {
      selectedText = window.getSelection().toString()

      if (selectedText && selectedText.length > 0) {
        logTelemetryEvent(CHAT_EVENTS.COPY_HISTORY, {
          selected_text: selectedText,
          timestamp: Date.now()
        })
      }
    }
  }

  return (
    <div className="cosmo" ref={containerRef}>
      <div className="cosmo__log" ref={logRef} onCopy={handleCopy}>
        {messages.map((m, i) => (
          <div key={i} className={`cosmo__msg cosmo__msg--${m.role}`}>
            <div className="cosmo__bubble">{m.text}</div>
          </div>
        ))}
        {busy && <div className="cosmo__msg cosmo__msg--assistant"><div className="cosmo__bubble cosmo__typing"><span/><span/><span/></div></div>}
      </div>
      <div className="cosmo__composer">
        <input className="cosmo__input" placeholder="Ask Cosmo…" value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onSelect={handleInputSelect}
          onPaste={handlePaste}
          onCopy={handleCopy}
          disabled={busy} />
        <button className="cosmo__send" onClick={send} disabled={busy || !input.trim()}>Send</button>
      </div>
    </div>
  )
}
